/**
 * Client-side file export handlers — CSV, PDF, DOCX.
 *
 * Takes tabular data (array of objects or markdown table text) and generates
 * a downloadable file. Zero LLM roundtrip — pure browser operation on
 * already-fetched data in the chat feed.
 */
import Papa from "papaparse";
import jsPDF from "jspdf";
import { Document, Packer, Paragraph, Table, TableRow, TableCell, HeadingLevel, TextRun } from "docx";
import { saveAs } from "file-saver";

export type ExportFormat = "csv" | "pdf" | "docx";

/** Parse a raw CSV string (e.g. from a ```csv code block) into rows + headers. */
export function parseCSVString(csv: string): { headers: string[]; rows: string[][] } | null {
  if (!csv || !csv.trim()) return null;
  const parsed = Papa.parse<string[]>(csv.trim(), { skipEmptyLines: true });
  if (!parsed.data || parsed.data.length === 0) return null;
  const [headers, ...rows] = parsed.data;
  return { headers: headers || [], rows: rows || [] };
}

/** Save a raw text string as a file (no parsing — for already-formatted content). */
export function downloadRawText(content: string, filename: string, mime: string = "text/plain"): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const { base, ext } = _splitExt(filename);
  saveAs(blob, sanitizeFilename(base) + (ext ? "." + ext : ".txt"));
}

/** Sanitize a string to be a safe filename — strip path chars, control chars, spaces.
 *  Uses plain string operations only (no regex). */
export function sanitizeFilename(name: string, maxLen: number = 80): string {
  if (!name) return "export";
  const ILLEGAL = new Set(["\\", "/", ":", "*", "?", '"', "<", ">", "|"]);
  const out: string[] = [];
  let prevUnderscore = false;
  for (const ch of name) {
    const code = ch.charCodeAt(0);
    // Strip control chars (0-31, 127)
    if (code < 32 || code === 127) continue;
    // Replace illegal filename chars + whitespace with a single underscore
    if (ILLEGAL.has(ch) || ch === " " || ch === "\t" || ch === "\n") {
      if (!prevUnderscore) { out.push("_"); prevUnderscore = true; }
      continue;
    }
    out.push(ch);
    prevUnderscore = false;
  }
  // Trim leading/trailing underscores
  let result = out.join("");
  while (result.startsWith("_")) result = result.slice(1);
  while (result.endsWith("_")) result = result.slice(0, -1);
  // Cap length
  if (result.length > maxLen) result = result.slice(0, maxLen);
  return result || "export";
}

/** Split a filename into base + extension using plain string ops (no regex). */
function _splitExt(filename: string): { base: string; ext: string } {
  const lastDot = filename.lastIndexOf(".");
  // No dot, dot at start (hidden file), or dot at end → treat whole as base
  if (lastDot <= 0 || lastDot === filename.length - 1) {
    return { base: filename, ext: "" };
  }
  const ext = filename.slice(lastDot + 1).toLowerCase();
  // Sanity check: extensions are 1-8 alphanumeric chars
  if (ext.length === 0 || ext.length > 8) return { base: filename, ext: "" };
  for (const c of ext) {
    if (!((c >= "a" && c <= "z") || (c >= "0" && c <= "9"))) {
      return { base: filename, ext: "" };
    }
  }
  return { base: filename.slice(0, lastDot), ext };
}

/** Export data as CSV. UTF-8 BOM included so Excel renders unicode correctly. */
export function exportToCSV(
  data: { headers: string[]; rows: string[][] },
  filename: string = "export.csv",
): void {
  const csv = Papa.unparse({
    fields: data.headers,
    data: data.rows,
  });
  // BOM (\ufeff) tells Excel to read the file as UTF-8 — without it, special
  // characters and accented names show up as mojibake.
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const { base } = _splitExt(filename);
  saveAs(blob, sanitizeFilename(base) + ".csv");
}

/** Export data as PDF — branded to match server-side render. */
export function exportToPDF(
  data: { headers: string[]; rows: string[][] },
  filename: string = "export.pdf",
  title: string = "Export",
): void {
  const BRAND = "ncoder.ai";
  const BRAND_HEX: [number, number, number] = [124, 58, 237]; // #7c3aed
  const STRIPE_HEX: [number, number, number] = [248, 250, 252]; // #f8fafc
  const MUTED_HEX: [number, number, number] = [107, 114, 128]; // #6b7280
  const ts = new Date().toISOString().replace("T", " ").slice(0, 19) + " UTC";

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const colWidth = (pageWidth - margin * 2) / data.headers.length;

  function drawPageChrome() {
    // Diagonal watermark
    doc.saveGraphicsState();
    doc.setFontSize(60);
    doc.setTextColor(229, 231, 235); // #e5e7eb
    doc.text(BRAND, pageWidth / 2, pageHeight / 2, { align: "center", angle: 35 });
    doc.restoreGraphicsState();
    // Footer band
    doc.setFillColor(...BRAND_HEX);
    doc.rect(0, 0, pageWidth, 18, "F");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(BRAND, margin * 0.4, 12);
    const pageNum = (doc as any).internal.getCurrentPageInfo().pageNumber;
    doc.text(`Page ${pageNum} • ${ts}`, pageWidth - margin * 0.4, 12, { align: "right" });
  }

  // Title + subtitle
  let y = margin;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND_HEX);
  doc.text(title, margin, y);
  y += 18;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...MUTED_HEX);
  doc.text(`${data.rows.length.toLocaleString()} rows • ${data.headers.length} columns`, margin, y);
  y += 16;

  // Header row — purple background
  doc.setFillColor(...BRAND_HEX);
  doc.rect(margin, y - 10, pageWidth - margin * 2, 14, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  data.headers.forEach((h, i) => {
    doc.text(String(h).slice(0, 24), margin + i * colWidth + 3, y);
  });
  y += 10;

  // Data rows — striped
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  let rowIdx = 0;
  for (const row of data.rows) {
    if (y > pageHeight - 30) {
      drawPageChrome();
      doc.addPage();
      y = margin;
    }
    if (rowIdx % 2 === 1) {
      doc.setFillColor(...STRIPE_HEX);
      doc.rect(margin, y - 8, pageWidth - margin * 2, 12, "F");
    }
    doc.setTextColor(0, 0, 0);
    row.forEach((cell, i) => {
      doc.text(String(cell ?? "").slice(0, 40), margin + i * colWidth + 3, y);
    });
    y += 12;
    rowIdx++;
  }
  drawPageChrome();

  const { base } = _splitExt(filename);
  doc.save(sanitizeFilename(base) + ".pdf");
}

/** Export data as DOCX — branded to match server-side render. */
export async function exportToDOCX(
  data: { headers: string[]; rows: string[][] },
  filename: string = "export.docx",
  title: string = "Export",
): Promise<void> {
  const BRAND = "ncoder.ai";
  const ts = new Date().toISOString().replace("T", " ").slice(0, 19) + " UTC";

  const headerRow = new TableRow({
    children: data.headers.map(
      (h) =>
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: String(h).slice(0, 60),
                  bold: true,
                  size: 18, // 9pt
                  color: "FFFFFF",
                }),
              ],
              shading: { fill: "7C3AED" },
            }),
          ],
          shading: { fill: "7C3AED", type: "clear", color: "auto" },
        }),
    ),
  });
  const dataRows = data.rows.map(
    (row, rowIdx) =>
      new TableRow({
        children: row.map(
          (cell) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: String(cell ?? "").slice(0, 80),
                      size: 16, // 8pt
                    }),
                  ],
                }),
              ],
              shading: rowIdx % 2 === 1
                ? { fill: "F8FAFC", type: "clear", color: "auto" }
                : undefined,
            }),
        ),
      }),
  );
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: { width: 15840, height: 12240, orientation: "landscape" as any },
            margin: { top: 720, bottom: 600, left: 720, right: 720 },
          },
        },
        headers: {
          default: {
            options: { children: [
              new Paragraph({
                children: [
                  new TextRun({ text: BRAND, bold: true, color: "7C3AED", size: 22 }),
                  new TextRun({ text: `   |   ${ts}`, size: 16, color: "6B7280" }),
                ],
              }),
            ]},
          },
        },
        footers: {
          default: {
            options: { children: [
              new Paragraph({
                alignment: "center" as any,
                children: [
                  new TextRun({ text: `Generated by ${BRAND}`, size: 16, color: "6B7280" }),
                ],
              }),
            ]},
          },
        },
        children: [
          new Paragraph({
            spacing: { after: 80 },
            children: [
              new TextRun({ text: title, bold: true, size: 28, color: "7C3AED" }),
            ],
          }),
          new Paragraph({
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: `${data.rows.length.toLocaleString()} rows • ${data.headers.length} columns`,
                size: 18,
                color: "6B7280",
              }),
            ],
          }),
          new Table({ rows: [headerRow, ...dataRows] }),
        ],
      },
    ],
  });
  const blob = await Packer.toBlob(doc);
  const { base } = _splitExt(filename);
  saveAs(blob, sanitizeFilename(base) + ".docx");
}
